import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  CircularProgress,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ACCENT, ACCENT_HOVER } from './discussionUtils';
import DiscussionPostingGuidelines from './DiscussionPostingGuidelines';

const CODE_SNIPPET = '```javascript\n// your code\n```';

const DiscussionComposer = ({
  placeholder = 'Write your message…',
  submitLabel = 'Post',
  autoFocus = false,
  compact = false,
  showCancel = false,
  showGuidelines = true,
  guidelineVariant = 'comment',
  onCancel,
  onSubmit,
  isDark,
  border,
  /** LeetCode-style auto @mention when replying */
  mention = null,
}) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mentionActive, setMentionActive] = useState(false);
  const inputRef = useRef(null);
  const mentionRef = useRef(null);

  useEffect(() => {
    if (mention?.username) {
      const prefix = `@${mention.username} `;
      mentionRef.current = prefix;
      setMentionActive(true);
      setText(prefix);
      if (autoFocus) {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    } else {
      mentionRef.current = null;
      setMentionActive(false);
      if (!text.startsWith('@')) setText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mention?.username, autoFocus]);

  const clearMention = () => {
    const prefix = mentionRef.current;
    setMentionActive(false);
    mentionRef.current = null;
    if (prefix && text.startsWith(prefix)) {
      setText(text.slice(prefix.length));
    }
    inputRef.current?.focus();
  };

  const insertCodeBlock = () => {
    setText((prev) => {
      const prefix = prev && !prev.endsWith('\n') ? '\n' : '';
      return `${prev}${prefix}\n${CODE_SNIPPET}\n`;
    });
    inputRef.current?.focus();
  };

  const handleSubmit = async () => {
    const body = text.trim();
    if (body.length < 2 || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(body);
      setText('');
      setMentionActive(false);
      mentionRef.current = null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      data-discussion-composer
      sx={{
        borderRadius: 2,
        border: `1px solid ${border}`,
        bgcolor: isDark ? '#0d1117' : '#fafbfc',
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:focus-within': {
          borderColor: alpha(ACCENT, 0.55),
          boxShadow: `0 0 0 3px ${alpha(ACCENT, isDark ? 0.15 : 0.1)}`,
        },
      }}
    >
      {showGuidelines && (
        <DiscussionPostingGuidelines
          variant={guidelineVariant}
          isDark={isDark}
          border={border}
        />
      )}
      {mentionActive && mention?.username && (
        <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            size="small"
            label={`@${mention.username}`}
            onDelete={clearMention}
            sx={{
              height: 24,
              fontWeight: 700,
              fontSize: '0.75rem',
              bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
              color: ACCENT,
            }}
          />
          <Typography variant="caption" sx={{ color: isDark ? '#8b949e' : '#5f6b73' }}>
            replying to {mention.name || mention.username}
          </Typography>
        </Box>
      )}

      <TextField
        inputRef={inputRef}
        multiline
        minRows={compact ? 2 : 3}
        maxRows={14}
        fullWidth
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        variant="standard"
        InputProps={{ disableUnderline: true }}
        sx={{
          px: 1.5,
          pt: mentionActive ? 0.5 : 1.25,
          pb: 0.5,
          '& textarea': { fontSize: '0.875rem', lineHeight: 1.55 },
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1,
          py: 0.75,
          borderTop: `1px solid ${alpha(border, 0.8)}`,
          bgcolor: isDark ? '#161b22' : '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title="Insert code block">
            <IconButton size="small" onClick={insertCodeBlock}>
              <CodeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Chip
            label="⌘↵ to post"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 600,
              bgcolor: 'transparent',
              color: isDark ? '#8b949e' : '#5f6b73',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {showCancel && (
            <Button
              size="small"
              onClick={onCancel}
              startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={submitting || text.trim().length < 2}
            onClick={handleSubmit}
            endIcon={
              submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendRoundedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: ACCENT_HOVER },
            }}
          >
            {submitting ? 'Posting…' : submitLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DiscussionComposer;
